import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  XMarkIcon,
  TrashIcon,
  CheckIcon,
  ChevronRightIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  DocumentIcon,
  SparklesIcon,
  PencilIcon,
  ClipboardDocumentIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../App';
import apiClient from '../../utils/apiClient';
import DocumentViewer from '../DocumentViewer';
import ConfirmationModal from '../ConfirmationModal';
import AttributeLabel from '../AttributeLabel';
import { Button } from '../Button';
import { ModalShell } from './core';
import type { ModalCloseReason } from './core';
import StatementDetailModal from './StatementDetailModal';
import { formatDocumentAmount, parseDocumentAmount } from '../../utils/documentFields';
import { useDocumentSession } from '../../utils/useDocumentSession';

interface DocumentDetailModalProps {
  documentId: number;
  onClose: () => void;
  onDelete: (id: number) => void;
  onAIScan: (docId: number) => void;
  onDownload: (docId: number, fileName: string) => void;
  onDocumentUpdated?: () => void;
  /** Öffnet das KI-Ergebnis-Modal für dieses Dokument. */
  onOpenAi?: (doc: any) => void;
  /**
   * Wert erhöhen, wenn das Dokument außerhalb dieses Modals verändert wurde
   * (KI-Modal geschlossen, Werte übernommen, Bild gedreht). Das Modal lädt
   * dann still nach — vorher waren Betrag und Datum erst nach Schließen und
   * erneutem Öffnen aktuell.
   */
  refreshToken?: number;
}

/** Reiter des Detail-Modals (Design-Entwurf „Dokumente Klickdummy"). */
// Kein 'vorschau'-Tab: die Vorschau steht dauerhaft in der linken Spalte.
type DetailTab = 'details' | 'buchungen';
type Feedback = { type: 'success' | 'error' | 'info'; message: string };

/** Betrag zum Bearbeiten deutsch darstellen (26.09 -> "26,09"). */
const betragZumBearbeiten = formatDocumentAmount;

/** Technische Upload-Suffixe und Unterstriche aus dem sichtbaren Titel entfernen. */
const lesbarerDokumenttitel = (dateiname: string): string => {
  const ohneEndung = dateiname.replace(/\.[^.]+$/, '');
  return ohneEndung
    .replace(/_+/g, ' ')
    .replace(/\s*\d{14}$/, '')
    .replace(/\s+/g, ' ')
    .trim() || dateiname;
};

interface LinkedBooking {
  buchung_id: number;
  konto_id: number;
  buchungstag: string;
  name_zahlungsbeteiligter: string;
  buchungstext: string;
  verwendungszweck: string;
  betrag: number;
  saldo: number;
  von_konto_id: number | null;
  nach_konto_id: number | null;
}

interface DocumentDetails {
  id: number;
  name: string;
  /** Optionale, eindeutige Belegnummer (steht auf dem Original / der Klarsichtfolie). */
  beleg_nr?: string | null;
  file_path: string;
  created_at: string;
  member_id: string;
  member_name?: string;
  size: number;
  tag?: string;
  ai_comment?: string;
  invoice_date?: string | null;
  invoice_amount?: number | null;
  is_linked?: number;
  linked_bookings?: LinkedBooking[];
}

const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  documentId,
  onClose,
  onDelete,
  onAIScan,
  onDownload,
  onDocumentUpdated,
  onOpenAi,
  refreshToken = 0,
}) => {
  const { BASE_URL } = useContext(AuthContext);
  const isCurrentSession = useDocumentSession(String(documentId));
  const detailRequest = useRef(0);
  const [tab, setTab] = useState<DetailTab>('details');
  const [previewError, setPreviewError] = useState(false);
  const [document, setDocument] = useState<DocumentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [isEditingAmount, setIsEditingAmount] = useState(false);
  const [editedAmount, setEditedAmount] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isEditingBelegNr, setIsEditingBelegNr] = useState(false);
  const [editedBelegNr, setEditedBelegNr] = useState('');
  const [belegNrError, setBelegNrError] = useState<string | null>(null);
  const [belegNrSaving, setBelegNrSaving] = useState(false);
  const [belegNrSuggestionLoading, setBelegNrSuggestionLoading] = useState(false);
  const [amountError, setAmountError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [amountSaving, setAmountSaving] = useState(false);
  const [nameSaving, setNameSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [selectedStatementId, setSelectedStatementId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const tabRefs = useRef<Record<DetailTab, HTMLButtonElement | null>>({ details: null, buchungen: null });
  /** Zähler zum Cache-Brechen der Vorschau, nachdem die Datei ersetzt wurde. */
  // Zeitstempel statt 0: sonst liefert der Browser-Cache nach einer Drehung
  // das alte Bild, weil die URL beim nächsten Öffnen identisch wäre.
  const [previewVersion, setPreviewVersion] = useState(() => Date.now());

  useEffect(() => {
    if (documentId) {
      setTab('details');
      setError(null);
      setFeedback(null);
      setShowMoreActions(false);
      setIsEditingAmount(false);
      setIsEditingName(false);
      setIsEditingBelegNr(false);
      setAmountError(null);
      setNameError(null);
      setBelegNrError(null);
      setDocument(null);
      setPreviewError(false);
      setShowViewer(false);
      setSelectedStatementId(null);
      setShowDeleteModal(false);
      setAmountSaving(false);
      setNameSaving(false);
      setBelegNrSaving(false);
      setBelegNrSuggestionLoading(false);
      fetchDocumentDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 3500);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  // Änderungen von außen (KI-Modal, Drehung) still nachladen: kein
  // Lade-Spinner, aber Vorschau neu ziehen, falls die Datei gedreht wurde.
  useEffect(() => {
    if (!documentId || refreshToken === 0) return;
    // Datei könnte gedreht worden sein: Vorschau-URL ändern, sonst bleibt das alte Bild.
    fetchDocumentDetails({ still: true });
    setPreviewVersion(version => version + 1);
    setPreviewError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const fetchDocumentDetails = async ({ still = false }: { still?: boolean } = {}) => {
    const request = ++detailRequest.current;
    const active = () => isCurrentSession() && request === detailRequest.current;
    try {
      if (!still) {
        setLoading(true);
        setError(null);
      }
      const response = await apiClient.get(`/api/documents/${documentId}`);
      if (!active()) return;
      setDocument(response.data);
      setError(null);
      setEditedAmount(betragZumBearbeiten(response.data.invoice_amount));
      setEditedName(response.data.name || '');
      setEditedBelegNr(response.data.beleg_nr || '');
    } catch (err) {
      if (!active()) return;
      console.error('Error fetching document details:', err);
      setError('Dokument konnte nicht geladen werden');
    } finally {
      if (active()) setLoading(false);
    }
  };

  const handleClose = (_reason: ModalCloseReason) => {
    onClose();
  };

  /**
   * Immer ins KI-Modal: das liest den Beleg selbst, wenn noch keine Analyse
   * vorliegt. Vorher gab es hier einen stillen Zweitweg ohne Ergebnisanzeige.
   */
  const belegPruefen = () => {
    if (!document) return;
    if (onOpenAi) onOpenAi(document);
    else onAIScan(document.id);
  };

  const abbrechenBetrag = () => {
    setIsEditingAmount(false);
    setAmountError(null);
    setEditedAmount(betragZumBearbeiten(document?.invoice_amount));
  };

  const handleSaveAmount = async () => {
    if (!document || amountSaving) return;
    const eingabe = editedAmount.trim();
    const amount = parseDocumentAmount(eingabe);
    if (eingabe && amount === null) {
      setAmountError('Bitte einen gültigen Betrag mit höchstens zwei Nachkommastellen eingeben.');
      return;
    }

    setAmountSaving(true);
    setAmountError(null);
    try {
      await apiClient.put(`/api/documents/${document.id}`, { invoice_amount: amount });
      if (!isCurrentSession()) return;
      setDocument(current => current?.id === document.id ? { ...current, invoice_amount: amount } : current);
      setIsEditingAmount(false);
      setFeedback({ type: 'success', message: 'Belegbetrag wurde gespeichert.' });
      onDocumentUpdated?.();
    } catch (err) {
      if (!isCurrentSession()) return;
      console.error('Error updating invoice amount:', err);
      setAmountError('Belegbetrag konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      if (isCurrentSession()) setAmountSaving(false);
    }
  };

  const handleSaveName = async () => {
    if (!document || nameSaving) return;
    const newName = editedName.trim();
    if (!newName) {
      setNameError('Der Dateiname darf nicht leer sein.');
      return;
    }
    if (newName === document.name) {
      setIsEditingName(false);
      setNameError(null);
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      await apiClient.put(`/api/documents/${document.id}`, { name: newName });
      if (!isCurrentSession()) return;
      setDocument(current => current?.id === document.id ? { ...current, name: newName } : current);
      setIsEditingName(false);
      setFeedback({ type: 'success', message: 'Dateiname wurde gespeichert.' });
      onDocumentUpdated?.();
    } catch (err) {
      if (!isCurrentSession()) return;
      console.error('Error updating document name:', err);
      setNameError('Dateiname konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      if (isCurrentSession()) setNameSaving(false);
    }
  };

  const abbrechenBelegNr = () => {
    setIsEditingBelegNr(false);
    setBelegNrError(null);
    setEditedBelegNr(document?.beleg_nr || '');
  };

  /**
   * Belegnummer speichern. Leer = Nummer entfernen. Das Backend prüft die
   * Eindeutigkeit und antwortet bei Doppelung mit 409 samt Gegenstück.
   */
  const handleSaveBelegNr = async () => {
    if (!document || belegNrSaving) return;
    const neu = editedBelegNr.trim();
    if (neu === (document.beleg_nr || '')) {
      abbrechenBelegNr();
      return;
    }
    setBelegNrSaving(true);
    setBelegNrError(null);
    try {
      await apiClient.put(`/api/documents/${document.id}`, { beleg_nr: neu });
      if (!isCurrentSession()) return;
      setDocument(current => current?.id === document.id ? { ...current, beleg_nr: neu || null } : current);
      setIsEditingBelegNr(false);
      setFeedback({
        type: 'success',
        message: neu ? 'Belegnummer wurde gespeichert.' : 'Belegnummer wurde entfernt.',
      });
      onDocumentUpdated?.();
    } catch (err: any) {
      if (!isCurrentSession()) return;
      const msg = err?.response?.data?.error;
      setBelegNrError(
        typeof msg === 'string' && msg ? msg : 'Belegnummer konnte nicht gespeichert werden',
      );
    } finally {
      if (isCurrentSession()) setBelegNrSaving(false);
    }
  };

  /** Nächste freie (numerische) Nummer vom Server holen und ins Feld setzen. */
  const belegNrVorschlagen = async () => {
    if (belegNrSuggestionLoading) return;
    setBelegNrSuggestionLoading(true);
    try {
      const { data } = await apiClient.get('/api/documents/beleg-nr/next');
      if (!isCurrentSession()) return;
      if (data?.beleg_nr) {
        setEditedBelegNr(String(data.beleg_nr));
        setBelegNrError(null);
      }
    } catch (err) {
      if (!isCurrentSession()) return;
      console.error('Error fetching next beleg_nr:', err);
      setBelegNrError('Vorschlag konnte nicht geladen werden');
    } finally {
      if (isCurrentSession()) setBelegNrSuggestionLoading(false);
    }
  };

  const formatSize = (bytes: number) => {
    if (!bytes || bytes === 0) return '–';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);
    return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(value)} ${sizes[i]}`;
  };

  const copyFilename = async () => {
    if (!document) return;
    try {
      await navigator.clipboard.writeText(document.name);
      setFeedback({ type: 'success', message: 'Dateiname wurde kopiert.' });
    } catch (err) {
      console.error('Error copying document name:', err);
      setFeedback({ type: 'error', message: 'Dateiname konnte nicht kopiert werden.' });
    }
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, current: DetailTab) => {
    const order: DetailTab[] = ['details', 'buchungen'];
    let next: DetailTab | null = null;
    if (event.key === 'ArrowRight') next = order[(order.indexOf(current) + 1) % order.length];
    if (event.key === 'ArrowLeft') next = order[(order.indexOf(current) - 1 + order.length) % order.length];
    if (event.key === 'Home') next = order[0];
    if (event.key === 'End') next = order[order.length - 1];
    if (!next) return;
    event.preventDefault();
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    const formatted = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(Math.abs(amount));
    return amount >= 0 ? `+${formatted.replace('€', 'EUR')}` : `-${formatted.replace('€', 'EUR')}`;
  };

  /** Thumbnail-URL des Belegs (gleiche Quelle wie die Kartenvorschau). */
  const previewUrl = (() => {
    if (!document) return '';
    const token = localStorage.getItem('jwt_token');
    const base = BASE_URL || localStorage.getItem('club_url') || 'http://localhost:4000';
    // `v` bricht den Browser-Cache auf, nachdem die Datei ersetzt wurde
    // (Zuschneiden oder Bildkorrektur) und ein neues Thumbnail existiert.
    return `${base}/api/documents/download-url/${document.id}?thumbnail=true&token=${token || ''}&v=${previewVersion}`;
  })();

  const linkedBookings = Array.from(
    new Map((document?.linked_bookings || []).map(booking => [booking.buchung_id, booking])).values(),
  );
  // MySQL liefert DECIMAL als String ("0.00") - der ist truthy. Deshalb als Zahl pruefen.
  const betragZahl = Number(document?.invoice_amount);
  const hasAmount = Number.isFinite(betragZahl) && betragZahl !== 0;
  const hasAIComment = document?.ai_comment;

  /**
   * Arbeitsstatus wie in der Kartenansicht: KI-Daten und Buchungsverknüpfung
   * entscheiden gemeinsam. Vorher urteilte der Kopf nur nach `ai_comment` und
   * zeigte „Neu" für bereits verbuchte Belege.
   */
  const hasBooking = document?.is_linked === 1 || linkedBookings.length > 0;
  const hasAiData = !!hasAIComment || !!hasAmount || !!document?.invoice_date;
  const workStatus: 'neu' | 'pruefen' | 'analysiert' = !hasAiData
    ? 'neu'
    : hasBooking
    ? 'analysiert'
    : 'pruefen';
  const STATUS_LABEL = { neu: 'Neu', pruefen: 'KI-Vorschlag prüfen', analysiert: 'Verbucht' } as const;
  const STATUS_CLS = {
    neu: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
    pruefen: 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-300',
    analysiert: 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-400',
  } as const;
  const displayTitle = document ? lesbarerDokumenttitel(document.name) : undefined;
  const primaryActionLabel = workStatus === 'neu'
    ? 'Mit KI auslesen'
    : workStatus === 'pruefen'
      ? 'KI-Vorschlag prüfen'
      : 'KI-Ergebnis ansehen';
  const primaryActionShortLabel = workStatus === 'neu'
    ? 'KI auslesen'
    : workStatus === 'pruefen'
      ? 'KI prüfen'
      : 'KI-Ergebnis';

  if (!document && !loading && !error) return null;

  return (
    <>
      <ModalShell
        open={Boolean(documentId)}
        onClose={handleClose}
        variant="detail"
        size="xl"
        accent="brand"
        showMobileGrabber
        bodyClassName="p-0"
        title={displayTitle}
        headerActions={
          document ? (
            <div className="relative sm:hidden">
              <button
                type="button"
                onClick={() => setShowMoreActions(open => !open)}
                onKeyDown={event => {
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowMoreActions(false);
                  }
                }}
                aria-label="Weitere Dokumentaktionen"
                aria-haspopup="menu"
                aria-expanded={showMoreActions}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
              </button>
              {showMoreActions && (
                <div
                  role="menu"
                  className="absolute right-0 top-12 z-20 min-w-[190px] rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg dark:border-gray-700 dark:bg-gray-800"
                >
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setShowMoreActions(false);
                      setShowDeleteModal(true);
                    }}
                    onKeyDown={event => {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        event.stopPropagation();
                        setShowMoreActions(false);
                      }
                    }}
                    className="flex min-h-[40px] w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-semibold text-error-600 hover:bg-error-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-500/40 dark:text-error-400 dark:hover:bg-error-900/20"
                  >
                    <TrashIcon className="h-4 w-4" aria-hidden="true" />
                    Dokument löschen
                  </button>
                </div>
              )}
            </div>
          ) : undefined
        }
        subtitle={
          document ? (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span>
                {document.file_path.split('.').pop()?.toUpperCase()} · {formatSize(document.size)} ·{' '}
                {document.member_name || 'Unbekannt'}
              </span>
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_CLS[workStatus]}`}
              >
                {workStatus === 'analysiert' && <CheckIcon className="h-3 w-3" />}
                {STATUS_LABEL[workStatus]}
              </span>
            </span>
          ) : undefined
        }
        footer={
          document ? (
            <div className="flex items-center gap-2.5">
              <button
                onClick={() => setShowDeleteModal(true)}
                className="hidden h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-error-50 text-error-600 ring-1 ring-inset ring-error-200 transition-colors hover:bg-error-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error-500/40 dark:bg-error-900/20 dark:text-error-400 dark:ring-error-800 dark:hover:bg-error-900/40 sm:flex"
                title="Dokument löschen"
                aria-label="Dokument löschen"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
              <div className="ml-auto grid min-w-0 flex-1 grid-cols-2 gap-2 sm:flex sm:flex-none sm:items-center sm:gap-3">
                <Button
                  variant="tertiary"
                  onClick={onClose}
                  className="min-h-[44px] w-full px-3 text-sm sm:w-auto sm:px-4 sm:text-base"
                >
                  Schließen
                </Button>
                <Button
                  variant="primary"
                  onClick={belegPruefen}
                  icon={<SparklesIcon aria-hidden="true" />}
                  className="min-h-[44px] w-full px-3 text-sm sm:w-auto sm:px-4 sm:text-base"
                >
                  <span className="sm:hidden">{primaryActionShortLabel}</span>
                  <span className="hidden sm:inline">{primaryActionLabel}</span>
                </Button>
              </div>
            </div>
          ) : undefined
        }
      >
        {loading ? (
          <div className="flex h-64 flex-col items-center justify-center gap-3" role="status" aria-live="polite">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600 dark:border-gray-600" aria-hidden="true" />
            <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">Dokument wird geladen …</p>
          </div>
        ) : error ? (
          <div className="flex h-64 flex-col items-center justify-center gap-4 px-6 text-center" role="alert">
            <div>
              <DocumentIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" aria-hidden="true" />
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">{error}</p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Prüfe die Verbindung und versuche es erneut.</p>
            </div>
            <Button variant="tertiary" size="sm" onClick={() => fetchDocumentDetails()}>
              Erneut versuchen
            </Button>
          </div>
        ) : document ? (
          <div>
            {/* Reiter */}
            <div
              role="tablist"
              aria-label="Dokumentbereiche"
              className="flex gap-5 overflow-x-auto border-b border-gray-200 px-4 dark:border-gray-700 sm:px-5"
            >
              {([
                { key: 'details', label: 'Details', count: 0 },
                { key: 'buchungen', label: 'Buchungen', count: linkedBookings.length },
              ] as { key: DetailTab; label: string; count: number }[]).map(t => {
                const on = tab === t.key;
                return (
                  <button
                    key={t.key}
                    ref={element => { tabRefs.current[t.key] = element; }}
                    id={`document-detail-tab-${documentId}-${t.key}`}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    aria-controls={`document-detail-panel-${documentId}-${t.key}`}
                    tabIndex={on ? 0 : -1}
                    onClick={() => setTab(t.key)}
                    onKeyDown={event => handleTabKeyDown(event, t.key)}
                    className={`inline-flex min-h-[44px] shrink-0 items-center gap-2 border-b-2 pb-2.5 pt-1 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40 ${
                      on
                        ? 'border-primary-500 font-extrabold text-primary-700 dark:text-primary-400'
                        : 'border-transparent font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                    }`}
                  >
                    {t.label}
                    {t.count > 0 && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                          on
                            ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {t.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {feedback && (
              <div
                role={feedback.type === 'error' ? 'alert' : 'status'}
                aria-live="polite"
                className={`mx-4 mt-3 rounded-xl border px-3.5 py-2.5 text-sm font-semibold sm:mx-5 ${
                  feedback.type === 'success'
                    ? 'border-success-200 bg-success-50 text-success-800 dark:border-success-800 dark:bg-success-900/20 dark:text-success-200'
                    : feedback.type === 'error'
                      ? 'border-error-200 bg-error-50 text-error-800 dark:border-error-800 dark:bg-error-900/20 dark:text-error-200'
                      : 'border-info-200 bg-info-50 text-info-800 dark:border-info-800 dark:bg-info-900/20 dark:text-info-200'
                }`}
              >
                {feedback.message}
              </div>
            )}

            {/* Links Belegvorschau, rechts Inhalt (Design-Entwurf) */}
            <div className="grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]">
              <div className="flex flex-col gap-2.5 border-b border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40 md:sticky md:top-0 md:self-start md:border-b-0 md:border-r">
                <div className="flex min-h-[140px] flex-1 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 md:min-h-[260px]">
                  {previewError ? (
                    <div className="flex flex-col items-center gap-1 text-gray-400">
                      <DocumentIcon className="h-8 w-8" aria-hidden="true" />
                      <span className="text-[11px] font-semibold uppercase tracking-wide">
                        {document.file_path.split('.').pop()?.toUpperCase()}
                      </span>
                    </div>
                  ) : (
                    <img
                      src={previewUrl}
                      alt={`Vorschau von ${document.name}`}
                      className="max-h-[150px] w-full object-contain sm:max-h-[180px] md:max-h-[360px]"
                      onError={() => setPreviewError(true)}
                    />
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowViewer(true)}
                    title="Dokument in der Großansicht öffnen; die offizielle Kopie enthält eine Herkunftszeile des Vereins"
                    className="inline-flex min-h-[40px] flex-1 items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white text-xs font-bold text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <ArrowsPointingOutIcon className="h-4 w-4" aria-hidden="true" />
                    Groß anzeigen
                  </button>
                  <button
                    type="button"
                    onClick={() => onDownload(document.id, document.name)}
                    aria-label="Herunterladen"
                    title="Herunterladen"
                    className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-700 transition-colors hover:bg-primary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:bg-primary-900/25 dark:text-primary-300"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
                <p className="hidden text-center text-[10.5px] leading-relaxed text-gray-400 dark:text-gray-500 md:block">
                  Großansicht und Download enthalten eine dezente Herkunftszeile des Vereins.
                </p>
              </div>

              <div className="min-w-0 p-4 sm:p-5">
                {/* ───────── Details ───────── */}
                {tab === 'details' && (
                  <div
                    id={`document-detail-panel-${documentId}-details`}
                    role="tabpanel"
                    aria-labelledby={`document-detail-tab-${documentId}-details`}
                    className="space-y-4"
                  >
                    {/* Belegbetrag */}
                    <div
                      className={`rounded-xl border p-3.5 ${
                        hasAmount || isEditingAmount
                          ? 'border-gray-200 bg-white dark:border-gray-700 dark:bg-[#1c2432]'
                          : 'border-dashed border-warning-300 bg-warning-50/50 dark:border-warning-700/50 dark:bg-warning-900/10'
                      }`}
                    >
                      {isEditingAmount ? (
                        /* Aufbau bewusst wie im Anzeigezustand: Beschriftung oben,
                           Wert darunter. So springt beim Umschalten nichts. */
                        <div>
                          <AttributeLabel
                            iconKey="amount"
                            size="sm"
                            textClassName="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                          >
                            Belegbetrag
                          </AttributeLabel>

                          <div className="mt-1.5 flex items-center gap-2">
                            <div className="relative min-w-0 flex-1">
                              {/* type="text" statt "number": keine Browser-Pfeile,
                                  und das deutsche Komma bleibt eingebbar. */}
                              <input
                                type="text"
                                inputMode="decimal"
                                value={editedAmount}
                                onChange={e => {
                                  setEditedAmount(e.target.value);
                                  if (amountError) setAmountError(null);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveAmount();
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    abbrechenBetrag();
                                  }
                                }}
                                disabled={amountSaving}
                                aria-invalid={!!amountError}
                                aria-describedby={amountError ? `document-detail-amount-error-${documentId}` : undefined}
                                className={`h-11 w-full rounded-xl border bg-white pl-3 pr-9 text-right text-[19px] font-bold tabular-nums text-gray-900 focus:outline-none focus:ring-2 disabled:opacity-60 dark:bg-gray-800 dark:text-white ${
                                  amountError
                                    ? 'border-error-500 focus:border-error-500 focus:ring-error-500/25 dark:border-error-500'
                                    : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500/25 dark:border-gray-600'
                                }`}
                                autoFocus
                                placeholder="0,00"
                                aria-label="Belegbetrag in Euro"
                              />
                              <span
                                className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[15px] font-bold text-gray-400 dark:text-gray-500"
                                aria-hidden="true"
                              >
                                €
                              </span>
                            </div>

                            <button
                              type="button"
                              onClick={handleSaveAmount}
                              disabled={amountSaving}
                              aria-label="Betrag speichern"
                              title="Speichern"
                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:cursor-wait disabled:opacity-60"
                            >
                              {amountSaving ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                              ) : (
                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={abbrechenBetrag}
                              disabled={amountSaving}
                              aria-label="Abbrechen"
                              title="Abbrechen"
                              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                            >
                              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          </div>

                          {amountError ? (
                            <p id={`document-detail-amount-error-${documentId}`} className="mt-1.5 text-[11.5px] font-semibold text-error-600 dark:text-error-400" role="alert">
                              {amountError}
                            </p>
                          ) : (
                            <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                              <kbd className="font-sans font-semibold">Enter</kbd> speichert ·{' '}
                              <kbd className="font-sans font-semibold">Esc</kbd> bricht ab
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <AttributeLabel
                              iconKey="amount"
                              size="sm"
                              textClassName="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400"
                            >
                              Belegbetrag
                            </AttributeLabel>
                            {hasAmount ? (
                              <p className="mt-0.5 text-[22px] font-bold leading-tight tabular-nums text-gray-900 dark:text-white">
                                {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
                                  Math.abs(document.invoice_amount!),
                                )}
                              </p>
                            ) : (
                              <>
                                <p className="mt-0.5 text-[17px] font-bold leading-tight text-warning-700 dark:text-warning-300">
                                  Noch kein Betrag
                                </p>
                                <p className="mt-0.5 text-[11.5px] text-gray-500 dark:text-gray-400">
                                  Per KI-Analyse auslesen oder von Hand eintragen.
                                </p>
                              </>
                            )}
                          </div>
                          <button
                            type="button"
                            aria-label={hasAmount ? 'Belegbetrag bearbeiten' : 'Belegbetrag eintragen'}
                            onClick={() => {
                              setEditedAmount(betragZumBearbeiten(document.invoice_amount));
                              setAmountError(null);
                              setIsEditingAmount(true);
                            }}
                            className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-3 text-xs font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 ${
                              hasAmount
                                ? 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                                : 'bg-primary-600 text-white hover:bg-primary-700'
                            }`}
                          >
                            <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            {hasAmount ? 'Bearbeiten' : 'Betrag eintragen'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Belegnummer: optional, eindeutig; wird auf das Original
                        bzw. die Klarsichtfolie geschrieben. */}
                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Belegnummer
                      </p>
                      {isEditingBelegNr ? (
                        <div>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              value={editedBelegNr}
                              maxLength={30}
                              onChange={e => {
                                setEditedBelegNr(e.target.value);
                                if (belegNrError) setBelegNrError(null);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveBelegNr();
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  abbrechenBelegNr();
                                }
                              }}
                              disabled={belegNrSaving || belegNrSuggestionLoading}
                              className={`min-w-0 flex-1 rounded-lg border bg-white px-2.5 py-2 text-sm font-semibold tabular-nums text-gray-900 focus:outline-none focus:ring-2 dark:bg-gray-800 dark:text-white ${
                                belegNrError
                                  ? 'border-error-500 focus:ring-error-500/30 dark:border-error-500'
                                  : 'border-gray-300 focus:ring-primary-500 dark:border-gray-600'
                              }`}
                              autoFocus
                              placeholder="z. B. 42 oder 2026-017"
                              aria-label="Belegnummer"
                              aria-invalid={!!belegNrError}
                              aria-describedby={belegNrError ? `document-detail-receipt-error-${documentId}` : undefined}
                            />
                            <button
                              type="button"
                              onClick={belegNrVorschlagen}
                              disabled={belegNrSaving || belegNrSuggestionLoading}
                              className="h-10 shrink-0 rounded-lg border border-gray-300 bg-white px-2.5 text-xs font-bold text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:cursor-wait disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                              title="Nächste freie Nummer eintragen"
                            >
                              {belegNrSuggestionLoading ? 'Lädt …' : 'Nächste'}
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveBelegNr}
                              disabled={belegNrSaving}
                              aria-label="Belegnummer speichern"
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:cursor-wait disabled:opacity-60"
                            >
                              {belegNrSaving ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                              ) : (
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={abbrechenBelegNr}
                              disabled={belegNrSaving}
                              aria-label="Bearbeitung abbrechen"
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:opacity-60 dark:bg-gray-700 dark:text-gray-300"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          {belegNrError ? (
                            <p id={`document-detail-receipt-error-${documentId}`} className="mt-1.5 text-[11.5px] font-semibold text-error-600 dark:text-error-400" role="alert">
                              {belegNrError}
                            </p>
                          ) : (
                            <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                              Muss eindeutig sein · leer lassen entfernt die Nummer
                            </p>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setEditedBelegNr(document.beleg_nr || '');
                            setBelegNrError(null);
                            setIsEditingBelegNr(true);
                          }}
                          className={`group flex min-h-[40px] w-full items-center justify-between gap-2 rounded-lg border bg-white px-3 py-2 text-left hover:border-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:bg-gray-800 ${
                            document.beleg_nr
                              ? 'border-gray-300 dark:border-gray-600'
                              : 'border-dashed border-gray-300 dark:border-gray-600'
                          }`}
                          title={document.beleg_nr ? 'Belegnummer bearbeiten' : 'Belegnummer vergeben'}
                        >
                          {document.beleg_nr ? (
                            <span className="truncate text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                              {document.beleg_nr}
                            </span>
                          ) : (
                            <span className="truncate text-sm text-gray-400 dark:text-gray-500">
                              Keine Belegnummer
                            </span>
                          )}
                          <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400">
                            <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            <span className="hidden sm:inline">{document.beleg_nr ? 'Bearbeiten' : 'Vergeben'}</span>
                          </span>
                        </button>
                      )}
                    </div>

                    {/* Dateiname */}
                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Dateiname
                      </p>
                      {isEditingName ? (
                        <div>
                          <div className="flex items-center gap-1.5">
                            <input
                              type="text"
                              value={editedName}
                              onChange={e => {
                                setEditedName(e.target.value);
                                if (nameError) setNameError(null);
                              }}
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleSaveName();
                                }
                                if (e.key === 'Escape') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setIsEditingName(false);
                                  setNameError(null);
                                  setEditedName(document.name || '');
                                }
                              }}
                              disabled={nameSaving}
                              aria-label="Dateiname"
                              aria-invalid={!!nameError}
                              aria-describedby={nameError ? `document-detail-name-error-${documentId}` : undefined}
                              className={`min-h-[40px] min-w-0 flex-1 rounded-lg border bg-white px-2.5 py-2 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 disabled:opacity-60 dark:bg-gray-800 dark:text-white ${
                                nameError
                                  ? 'border-error-500 focus:ring-error-500/30 dark:border-error-500'
                                  : 'border-gray-300 focus:ring-primary-500 dark:border-gray-600'
                              }`}
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={handleSaveName}
                              disabled={nameSaving}
                              aria-label="Dateiname speichern"
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:cursor-wait disabled:opacity-60"
                            >
                              {nameSaving ? (
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true" />
                              ) : (
                                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                              )}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setIsEditingName(false);
                                setNameError(null);
                                setEditedName(document.name || '');
                              }}
                              disabled={nameSaving}
                              aria-label="Bearbeitung abbrechen"
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 disabled:opacity-60 dark:bg-gray-700 dark:text-gray-300"
                            >
                              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                            </button>
                          </div>
                          {nameError && (
                            <p id={`document-detail-name-error-${documentId}`} className="mt-1.5 text-[11.5px] font-semibold text-error-600 dark:text-error-400" role="alert">
                              {nameError}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex min-h-[40px] overflow-hidden rounded-lg border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800">
                          <button
                            type="button"
                            onClick={() => {
                              setNameError(null);
                              setIsEditingName(true);
                            }}
                            className="group flex min-w-0 flex-1 items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40 dark:hover:bg-gray-700"
                            title={document.name}
                          >
                            <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                              {document.name}
                            </span>
                            <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary-600 dark:text-primary-400">
                              <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" />
                              <span className="hidden sm:inline">Bearbeiten</span>
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={copyFilename}
                            className="flex w-10 shrink-0 items-center justify-center border-l border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500/40 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                            aria-label="Dateiname kopieren"
                            title="Dateiname kopieren"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Dokumentdetails */}
                    <div>
                      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Dokumentdetails
                      </p>
                      <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 sm:grid-cols-2">
                        {[
                          { label: 'Hochgeladen', value: formatDate(document.created_at) },
                          { label: 'Dateigröße', value: formatSize(document.size) },
                          { label: 'Format', value: document.file_path.split('.').pop()?.toUpperCase() || '–' },
                          { label: 'Hochgeladen von', value: document.member_name || 'Unbekannt' },
                        ].map(row => (
                          <div
                            key={row.label}
                            className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-b border-gray-200 px-3.5 py-2.5 last:border-b-0 dark:border-gray-700 sm:[&:nth-last-child(-n+2)]:border-b-0 sm:[&:nth-child(odd)]:border-r"
                          >
                            <span className="whitespace-nowrap text-[12.5px] text-gray-500 dark:text-gray-400">
                              {row.label}
                            </span>
                            <span className="min-w-0 break-words text-right text-[12.5px] font-semibold text-gray-900 dark:text-white">
                              {row.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* ───────── Buchungen ───────── */}
                {tab === 'buchungen' && (
                  <div
                    id={`document-detail-panel-${documentId}-buchungen`}
                    role="tabpanel"
                    aria-labelledby={`document-detail-tab-${documentId}-buchungen`}
                    className="space-y-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        Verweisende Buchungen
                      </p>
                      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                        {linkedBookings.length > 0 ? `${linkedBookings.length} verknüpft` : 'keine Verknüpfung'}
                      </span>
                    </div>

                    {linkedBookings.length > 0 ? (
                      <div className="space-y-2">
                        {linkedBookings.map(booking => (
                          <button
                            key={booking.buchung_id}
                            type="button"
                            onClick={() => setSelectedStatementId(String(booking.buchung_id))}
                            className="flex min-h-[44px] w-full items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13px] font-bold text-gray-900 dark:text-white">
                                {booking.name_zahlungsbeteiligter || booking.buchungstext}
                              </span>
                              <span className="block text-[11.5px] text-gray-500 dark:text-gray-400">
                                {formatDate(booking.buchungstag)}
                              </span>
                            </span>
                            <span
                              className={`shrink-0 text-[13px] font-bold tabular-nums ${
                                booking.betrag < 0
                                  ? 'text-error-600 dark:text-error-400'
                                  : 'text-success-700 dark:text-success-400'
                              }`}
                            >
                              {formatAmount(booking.betrag)}
                            </span>
                            <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden="true" />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed border-gray-300 p-4 dark:border-gray-600">
                        <p className="min-w-[200px] flex-1 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">
                          Noch keine Buchung verknüpft.
                          {hasAIComment
                            ? ' Die KI-Werte liegen bereit — daraus lässt sich eine Buchung anlegen.'
                            : ' Beim Prüfen liest die KI Betrag und Datum aus.'}
                        </p>
                        <button
                          type="button"
                          onClick={belegPruefen}
                          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-xs font-bold text-white hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40"
                        >
                          <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                          {primaryActionLabel}
                        </button>
                      </div>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        ) : null}
      </ModalShell>

      {/* Document Viewer */}
      {document && showViewer && (
        <DocumentViewer
          documentId={document.id}
          fileName={document.name}
          filePath={document.file_path}
          sourceUrl={`/api/documents/official-copy/${document.id}`}
          onClose={() => setShowViewer(false)}
          onDocumentUpdated={() => {
            // Datei wurde ersetzt: eigene Vorschau neu ziehen und die Liste
            // dahinter ebenfalls aktualisieren.
            setPreviewVersion(version => version + 1);
            setPreviewError(false);
            fetchDocumentDetails();
            onDocumentUpdated?.();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        visible={showDeleteModal}
        type="warning"
        title="Löschen bestätigen"
        message={`Möchten Sie das Dokument "${document?.name || 'dieses Dokument'}" wirklich löschen?`}
        variant="two"
        showCloseButton={true}
        onClose={() => setShowDeleteModal(false)}
        buttons={{
          cancel: {
            text: 'Abbrechen',
            onPress: () => setShowDeleteModal(false)
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              onDelete(document?.id || documentId);
              setShowDeleteModal(false);
              onClose();
            }
          }
        }}
      />

      {/* Buchungsdetail (aus verweisender Buchung geöffnet) */}
      {selectedStatementId && (
        <StatementDetailModal
          statementId={selectedStatementId}
          onClose={() => setSelectedStatementId(null)}
        />
      )}
    </>
  );
};

export default DocumentDetailModal;
